/**
 * Helpers for the IPO Calendar page: dates, month grids, calendar events derived
 * from issue schedules, issue phases and iCalendar (.ics) export.
 * Dates are ISO strings ('2026-09-21') for IST calendar days.
 */
import { toLocalIsoDate } from './gmpBoard';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_SHORT = MONTH_NAMES.map((name) => name.slice(0, 3));
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const pad = (n) => String(n).padStart(2, '0');

export const todayIso = (now = new Date()) => toLocalIsoDate(now);

export const parseIso = (iso) => {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const addDays = (iso, days) => {
  const date = parseIso(iso);
  date.setDate(date.getDate() + days);
  return toLocalIsoDate(date);
};

export const monthKeyOf = (iso) => iso.slice(0, 7);

export const shiftMonth = (monthKey, delta) => {
  const [year, month] = monthKey.split('-').map(Number);
  const date = new Date(year, month - 1 + delta, 1);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
};

export const formatMonthLabel = (monthKey, { short = false } = {}) => {
  const [year, month] = monthKey.split('-').map(Number);
  return `${(short ? MONTH_SHORT : MONTH_NAMES)[month - 1]} ${year}`;
};

// 'Monday, 21 September 2026'
export const formatDayHeading = (iso) => {
  const date = parseIso(iso);
  return `${WEEKDAY_NAMES[date.getDay()]}, ${pad(date.getDate())} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
};

// 'Sep 09'
export const formatDayShort = (iso) => {
  const date = parseIso(iso);
  return `${MONTH_SHORT[date.getMonth()]} ${pad(date.getDate())}`;
};

// 'Tue, 15 Sep'
export const formatWeekdayShort = (iso) => {
  const date = parseIso(iso);
  return `${WEEKDAY_NAMES[date.getDay()].slice(0, 3)}, ${date.getDate()} ${MONTH_SHORT[date.getMonth()]}`;
};

// '16 – 18 Sep' or '28 Aug – 02 Sep'
export const formatDateRange = (fromIso, toIso) => {
  const from = parseIso(fromIso);
  const to = parseIso(toIso);
  if (from.getMonth() === to.getMonth()) {
    return `${pad(from.getDate())} – ${pad(to.getDate())} ${MONTH_SHORT[to.getMonth()]}`;
  }
  return `${pad(from.getDate())} ${MONTH_SHORT[from.getMonth()]} – ${pad(to.getDate())} ${MONTH_SHORT[to.getMonth()]}`;
};

export const isWeekend = (iso) => [0, 6].includes(parseIso(iso).getDay());

export const nextBusinessDay = (iso, holidayDates = []) => {
  let next = addDays(iso, 1);
  while (isWeekend(next) || holidayDates.includes(next)) next = addDays(next, 1);
  return next;
};

// Month cells for a Monday-first grid; null pads the leading/trailing days
export const buildMonthGrid = (monthKey) => {
  const [year, month] = monthKey.split('-').map(Number);
  const offset = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = Array.from({ length: offset }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(`${monthKey}-${pad(day)}`);
  while (cells.length % 7) cells.push(null);
  return cells;
};

export const WEEKDAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
export const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const EVENT_TYPES = {
  open: { label: 'Opens', plural: 'Openings', singular: 'Opening' },
  close: { label: 'Closes', plural: 'Closings', singular: 'Closing' },
  allotment: { label: 'Allotment', plural: 'Allotments', singular: 'Allotment' },
  holiday: { label: 'Holiday', plural: 'Holidays', singular: 'Holiday' },
};

// Order events inside a day: the most time-sensitive first
const TYPE_ORDER = ['close', 'open', 'allotment', 'holiday'];

/**
 * Expands issue schedules into dated events. Issues without an explicit allotment
 * date get the next business day after closing (SEBI's T+1 basis of allotment).
 */
export const buildCalendarEvents = (issues, holidays = []) => {
  const holidayDates = holidays.map((holiday) => holiday.date);
  const issueEvents = issues.flatMap((raw) => {
    const allotmentOn = raw.allotmentOn || (raw.closeOn ? nextBusinessDay(raw.closeOn, holidayDates) : null);
    const issue = { ...raw, allotmentOn, allotmentEstimated: !raw.allotmentOn };
    return [
      issue.openOn && { id: `${issue.id}-open`, type: 'open', date: issue.openOn, issue },
      issue.closeOn && { id: `${issue.id}-close`, type: 'close', date: issue.closeOn, issue },
      allotmentOn && { id: `${issue.id}-allotment`, type: 'allotment', date: allotmentOn, issue },
    ].filter(Boolean);
  });
  const holidayEvents = holidays.map((holiday) => ({
    id: `holiday-${holiday.date}`,
    type: 'holiday',
    date: holiday.date,
    holiday: { ...holiday, resumesOn: nextBusinessDay(holiday.date, holidayDates) },
  }));
  return [...issueEvents, ...holidayEvents];
};

export const sortEventsInDay = (events) =>
  [...events].sort((a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type));

// [{ date, events }] newest day first
export const groupEventsByDay = (events) => {
  const byDay = new Map();
  events.forEach((event) => {
    if (!byDay.has(event.date)) byDay.set(event.date, []);
    byDay.get(event.date).push(event);
  });
  return [...byDay.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, dayEvents]) => ({ date, events: sortEventsInDay(dayEvents) }));
};

// Where an issue stands on a given day
export const getIssuePhase = (issue, today) => {
  if (today < issue.openOn) return 'upcoming';
  if (today <= issue.closeOn) return 'open';
  if (issue.allotmentOn && today >= issue.allotmentOn) return 'allotted';
  return 'closed';
};

export const countByType = (events) =>
  events.reduce((acc, event) => ({ ...acc, [event.type]: (acc[event.type] || 0) + 1 }), {});

// Days with the most bidding actions (openings + closings)
export const findRushDays = (events) => {
  const counts = new Map();
  events
    .filter((event) => event.type === 'open' || event.type === 'close')
    .forEach((event) => counts.set(event.date, (counts.get(event.date) || 0) + 1));
  const max = Math.max(0, ...counts.values());
  if (!max) return { dates: [], count: 0 };
  return {
    dates: [...counts.entries()].filter(([, count]) => count === max).map(([date]) => date).sort(),
    count: max,
  };
};

// --- iCalendar export -------------------------------------------------------

const IST_OFFSET_MINUTES = 330;
const BIDDING_OPENS_AT = [10, 0];
const BIDDING_CUTOFF_AT = [17, 0];

// IST wall-clock time -> UTC 'YYYYMMDDTHHMMSSZ'
const toUtcStamp = (iso, [hours, minutes]) => {
  const [year, month, day] = iso.split('-').map(Number);
  const utc = new Date(Date.UTC(year, month - 1, day, hours, minutes) - IST_OFFSET_MINUTES * 60000);
  return utc.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
};

const toDateStamp = (iso) => iso.replace(/-/g, '');

export const escapeIcsText = (text) =>
  String(text).replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

// RFC 5545 folds long lines; 60 characters keeps multi-byte text (₹) under 75 octets
const foldLine = (line) => {
  if (line.length <= 60) return line;
  const chunks = [];
  for (let i = 0; i < line.length; i += 60) chunks.push(line.slice(i, i + 60));
  return chunks.join('\r\n ');
};

const priceBand = (issue) => {
  if (!issue.priceHigh) return null;
  return issue.priceLow && issue.priceLow !== issue.priceHigh ? `₹${issue.priceLow} – ₹${issue.priceHigh}` : `₹${issue.priceHigh}`;
};

const describeEvent = (event) => {
  if (event.type === 'holiday') {
    return {
      summary: `Market holiday — ${event.holiday.name}`,
      description: event.holiday.note || 'BSE and NSE are closed.',
    };
  }
  const { issue } = event;
  const window = `Bidding window ${formatDateRange(issue.openOn, issue.closeOn)}`;
  if (event.type === 'open') {
    const band = priceBand(issue);
    const terms = [band ? `Price ${band}` : null, issue.lotSize ? `lot ${issue.lotSize} shares` : null].filter(Boolean);
    return {
      summary: `${issue.company} IPO opens`,
      description: terms.length ? `${window}. ${terms.join(', ')}.` : `${window}.`,
    };
  }
  if (event.type === 'close') {
    return {
      summary: `${issue.company} IPO closes — UPI cut-off 5:00 PM IST`,
      description: `${window}. Approve your UPI mandate before 5:00 PM IST.`,
    };
  }
  return {
    summary: `${issue.company} IPO allotment status`,
    description: `Basis of allotment by ${issue.registrar || 'the registrar'}. Check with PAN or application number.`,
  };
};

/**
 * Builds an .ics calendar for the given events. Openings and closings are timed
 * (closings carry a reminder 2 hours before the 5:00 PM cut-off); allotments and
 * holidays are all-day entries.
 */
export const buildIcsCalendar = (events, { now = new Date() } = {}) => {
  const stamp = now.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Alambrix IPO//IPO Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];

  events.forEach((event) => {
    const { summary, description } = describeEvent(event);
    lines.push('BEGIN:VEVENT', `UID:${event.id}@ipo.alambrix.ai`, `DTSTAMP:${stamp}`);
    if (event.type === 'open') {
      lines.push(`DTSTART:${toUtcStamp(event.date, BIDDING_OPENS_AT)}`, `DTEND:${toUtcStamp(event.date, [10, 30])}`);
    } else if (event.type === 'close') {
      lines.push(`DTSTART:${toUtcStamp(event.date, BIDDING_CUTOFF_AT)}`, `DTEND:${toUtcStamp(event.date, [17, 15])}`);
    } else {
      lines.push(`DTSTART;VALUE=DATE:${toDateStamp(event.date)}`, `DTEND;VALUE=DATE:${toDateStamp(addDays(event.date, 1))}`);
    }
    lines.push(`SUMMARY:${escapeIcsText(summary)}`, `DESCRIPTION:${escapeIcsText(description)}`);
    if (event.type === 'close') {
      lines.push('BEGIN:VALARM', 'ACTION:DISPLAY', `DESCRIPTION:${escapeIcsText(summary)}`, 'TRIGGER:-PT2H', 'END:VALARM');
    }
    lines.push('END:VEVENT');
  });

  lines.push('END:VCALENDAR');
  return `${lines.map(foldLine).join('\r\n')}\r\n`;
};
